import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { TbAdjustmentsHorizontal, TbBug, TbShieldCheck } from "react-icons/tb";
import useLabels from "../../hooks/useLabels";
import HeaderShell, { HeaderAction } from "@wine2trace/ui/HeaderShell";
import { format } from "date-fns";

import logo from "@wine2trace/ui/assets/W2T_LOGO_symbol.png";
import { TbBell } from "react-icons/tb";
import NotificationsCenter from "./Notifications/Notifications";
import { useRef, useState } from "react";
import useClickOutside from "../../hooks/useClickOutside";
import useLogout from "../../hooks/useLogout";
import { useNavigate } from "react-router";
import useAuth from "../../hooks/useAuth";
import usePermissions from "../../hooks/usePermissions";
import WinerySwitcher from "./WinerySwitcher";
import { getItem } from "../../services/LocalStorage";

import {
  selectActiveWinery,
  selectConfigs,
  selectUser,
  selectUserRbacLoadedFor,
  selectUsers,
  selectWineries,
} from "../../context/selectors";
import { ROLES } from "../../constants/base";
import { Icon } from "../../components/Icon";
import { formatUnreadBadge } from "./unreadBadge";

// App parts (winery switch, user menu entries, notifications) for the shared HeaderShell.
const Header = ({ isMobile, isConnected }) => {
  const [getLabel] = useLabels();
  const navigate = useNavigate();
  const logout = useLogout();
  const user = useSelector(selectUser);
  const wineries = useSelector(selectWineries);
  const users = useSelector(selectUsers);
  const activeWinery = useSelector(selectActiveWinery);
  const configs = useSelector(selectConfigs);

  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [counter, setCounter] = useState(0);
  const { auth } = useAuth();
  const { roleCodes } = usePermissions();
  const rbacLoadedFor = useSelector(selectUserRbacLoadedFor);
  const role = Number(auth?.role) || ROLES.GUEST;

  // Under the name: the role templates of the user in the active winery (the rbac of the winery just left is not
  // shown while the new one loads); the account type when there are none (platform ADMIN, tank monitor, not loaded).
  const wineryRoleCodes = user?.type !== "TANK" && rbacLoadedFor === activeWinery?.id ? roleCodes : [];
  // OWNER already grants everything: it is shown alone (the licence holder also holds CELLAR_ADMIN in every winery).
  const shownRoleCodes = wineryRoleCodes.includes("OWNER") ? ["OWNER"] : wineryRoleCodes;
  const roleText = shownRoleCodes.length
    ? shownRoleCodes.map((code) => getLabel(`user_subtype_${code}`)).join(", ")
    : [getLabel(user?.type), user?.type === "TANK" ? getItem("currentMonitor") : null].filter(Boolean).join(" ");

  const notificationWrapperRef = useRef();

  const envLabel =
    window.location.hostname === "stage-webapp.wine2trace.com"
      ? "Stage"
      : window.location.hostname === "localhost"
      ? "Test"
      : null;

  useClickOutside(notificationWrapperRef, () => {
    if (isNotificationOpen) {
      setIsNotificationOpen(false);
    }
  });

  const setNotificationOpen = () => {
    setIsNotificationOpen(!isNotificationOpen);
  };

  const profileId = auth?.user_id || users?.find((u) => u?.username === user?.userName)?.id || "";
  const canSwitchWinery = role !== ROLES.ADMIN && wineries?.length > 1 && user?.type !== "TANK";

  return (
    <HeaderShell
      isMobile={isMobile}
      logo={logo}
      formatDate={(date) => format(date, configs.dateFormat)}
      envLabel={envLabel}
      center={
        role !== ROLES.ADMIN && activeWinery?.name ? (
          <WinerySwitcher
            activeWinery={activeWinery}
            canSwitch={canSwitchWinery}
            locale={configs?.locale?.replace("_", "-")}
          />
        ) : null
      }
      account={{
        name: user?.userName,
        role: roleText,
        locale: configs?.locale?.replace("_", "-"),
        items: [
          role !== ROLES.ADMIN && {
            key: "personal-info",
            label: getLabel("settingsPersonalInfo"),
            element: (
              <Link to={profileId ? `/users/${profileId}/customize` : ""} className="header_menu_item">
                <Icon icon={TbAdjustmentsHorizontal} size="md" aria-hidden="true" />
                {getLabel("settingsPersonalInfo")}
              </Link>
            ),
          },
          // Account tier, not a grant: the audit route (routes/api/winery/audit.js) is verifyRole(CLIENT) with no permission.
          role === ROLES.CLIENT && {
            key: "audit",
            label: getLabel("settingsAuditLog"),
            element: (
              <Link to="/audit" className="header_menu_item">
                <Icon icon={TbShieldCheck} size="md" aria-hidden="true" />
                {getLabel("settingsAuditLog")}
              </Link>
            ),
          },
          role === ROLES.ADMIN && {
            key: "error-reports",
            label: getLabel("errorReportsNavLink"),
            element: (
              <Link to="/admin/error-reports" className="header_menu_item">
                <Icon icon={TbBug} size="md" aria-hidden="true" />
                {getLabel("errorReportsNavLink")}
              </Link>
            ),
          },
        ].filter(Boolean),
      }}
      // The platform ADMIN is not a client user: it has no profile page (null: no entry in the menu).
      onProfile={role === ROLES.ADMIN ? null : () => navigate(auth?.user_id ? `/users/${auth.user_id}` : "")}
      onLogout={logout}
    >
      {role !== ROLES.ADMIN && (
        <HeaderAction
          className={`header_wrapper_bell${counter > 0 ? " has_unread" : ""}`}
          icon={TbBell}
          label={getLabel("notifications")}
          onClick={setNotificationOpen}
          ref={notificationWrapperRef}
        >
          {counter > 0 && (
            <span className="trigger-span" aria-label={`${counter}`}>
              {formatUnreadBadge(counter)}
            </span>
          )}
          <NotificationsCenter
            isOpen={isNotificationOpen}
            setIsOpen={setIsNotificationOpen}
            setCounter={setCounter}
          />
          <Icon
            icon="fa-circle"
            size={"xxs"}
            className={`socket ${isConnected ? "connected" : ""} ${
              counter > 0 ? "fix" : ""
            }`}
          />
        </HeaderAction>
      )}
    </HeaderShell>
  );
};

export default Header;
